import { useCallback, useState } from 'react';

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  OrganiseDocumentStatus,
  OrganiseDocumentTag,
  isCallNoteDocument,
} from '@karehero/models';
import {
  formatFileSize,
  isAudioType,
  isImageType,
} from '@karehero/helpers/files';
import { formatDate } from '@karehero/helpers/datetime';

const { Scanning, Scanned, Rejected } = OrganiseDocumentStatus;

import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Icons } from '@/components/atoms/Icon/icons';
import { getDocumentTagColor } from '@/theme/mui/color';

export interface DocumentItem {
  id: string;
  title: string;
  description?: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: OrganiseDocumentStatus;
  tags?: OrganiseDocumentTag[];
  createdAt: string;
  downloadUrl?: string;
  hasExtractedFacts?: boolean;
  extractedAt?: string | null;
}

export interface KeyDocumentsPageProps {
  documents: DocumentItem[];
  isLoading?: boolean;
  onBack?: () => void;
  onUpload: () => void;
  onRecordAudio?: () => void;
  onDownload: (document: DocumentItem) => void;
  onDelete: (document: DocumentItem) => void;
  onCardClick?: (document: DocumentItem) => void;
  showExtractedFacts?: boolean;
  onViewFacts?: (document: DocumentItem) => void;
}

const getFileIcon = (contentType: string): Icons => {
  if (isAudioType(contentType)) return 'microphone';
  return 'file-contract';
};

export const KeyDocumentsPage = ({
  documents,
  isLoading,
  onBack,
  onUpload,
  onRecordAudio,
  onDownload,
  onDelete,
  onCardClick,
  showExtractedFacts,
  onViewFacts,
}: KeyDocumentsPageProps) => {
  const theme = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentItem | null>(
    null,
  );

  const handleDeleteClick = useCallback((doc: DocumentItem) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (documentToDelete) {
      onDelete(documentToDelete);
    }
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }, [documentToDelete, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }, []);

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title='Key Documents'
            description='Store important documents related to your care journey'
          />
        }
        contentSlot={
          <Stack spacing={3}>
            {/* Action buttons */}
            <Stack direction='row' spacing={1.5}>
              <Button
                variant='contained'
                startIcon={<Icon icon='upload' fontSize={18} />}
                onClick={onUpload}
                fullWidth
                size='large'
              >
                Upload Document
              </Button>
              {onRecordAudio && (
                <Button
                  variant='outlined'
                  startIcon={<Icon icon='microphone' fontSize={18} />}
                  onClick={onRecordAudio}
                  fullWidth
                  size='large'
                >
                  Record Audio
                </Button>
              )}
            </Stack>

            {/* Loading state */}
            {isLoading && (
              <Stack
                sx={{
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={32} />
              </Stack>
            )}

            {/* Document cards */}
            {!isLoading && documents.length > 0 && (
              <Stack spacing={2}>
                {documents.map((doc) => (
                  <Card
                    key={doc.id}
                    elevation={0}
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      cursor:
                        isCallNoteDocument(doc.tags) && onCardClick
                          ? 'pointer'
                          : 'default',
                    }}
                    onClick={
                      isCallNoteDocument(doc.tags) && onCardClick
                        ? () => onCardClick(doc)
                        : undefined
                    }
                  >
                    {doc.status === Scanning && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          bgcolor: 'rgba(255,255,255,0.6)',
                          zIndex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={28} />
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 500,
                            color: 'text.secondary',
                          }}
                        >
                          Processing
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        p: 2,
                        opacity: doc.status === Rejected ? 0.6 : 1,
                      }}
                    >
                      <Stack spacing={1.5}>
                        {/* Document header */}
                        <Stack
                          direction='row'
                          spacing={1.5}
                          sx={{
                            alignItems: 'flex-start',
                          }}
                        >
                          {isImageType(doc.contentType) && doc.downloadUrl ? (
                            <Box
                              component='img'
                              src={doc.downloadUrl}
                              alt={doc.title}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 1,
                                objectFit: 'cover',
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Icon
                              icon={getFileIcon(doc.contentType)}
                              fontSize={28}
                              color={theme.palette.primary.main}
                            />
                          )}
                          <Stack
                            sx={{
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant='subtitle1'
                              sx={{
                                fontWeight: 600,
                              }}
                            >
                              {doc.title}
                            </Typography>
                            {doc.description && (
                              <Typography
                                variant='body2'
                                sx={{
                                  color: 'text.secondary',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {doc.description}
                              </Typography>
                            )}
                          </Stack>
                          <Stack direction='row' spacing={0.5}>
                            {isCallNoteDocument(doc.tags) && onCardClick ? (
                              <IconButton
                                size='small'
                                onClick={() => onCardClick(doc)}
                                aria-label='View'
                              >
                                <Icon icon='chevron-right' fontSize={18} />
                              </IconButton>
                            ) : (
                              doc.status === Scanned && (
                                <IconButton
                                  size='small'
                                  onClick={() => onDownload(doc)}
                                  aria-label='Download'
                                >
                                  <Icon icon='download' fontSize={18} />
                                </IconButton>
                              )
                            )}
                            {showExtractedFacts &&
                              doc.hasExtractedFacts &&
                              onViewFacts && (
                                <IconButton
                                  size='small'
                                  onClick={() => onViewFacts(doc)}
                                  aria-label='View data'
                                >
                                  <Icon icon='clipboard-list' fontSize={18} />
                                </IconButton>
                              )}
                            {!isCallNoteDocument(doc.tags) && (
                              <IconButton
                                size='small'
                                onClick={() => handleDeleteClick(doc)}
                                aria-label='Delete'
                                color='error'
                              >
                                <Icon icon='trash' fontSize={18} />
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>

                        {/* Audio player */}
                        {isAudioType(doc.contentType) &&
                          doc.status === Scanned &&
                          doc.downloadUrl && (
                            <Box
                              component='audio'
                              controls
                              preload='none'
                              src={doc.downloadUrl}
                              sx={{ width: '100%', height: 40 }}
                            />
                          )}

                        {doc.status === Rejected && (
                          <Chip
                            label='Rejected — malware detected'
                            size='small'
                            color='error'
                            variant='filled'
                          />
                        )}

                        {/* Tags */}
                        {doc.tags && doc.tags.length > 0 && (
                          <Stack
                            direction='row'
                            spacing={0.5}
                            useFlexGap
                            sx={{
                              flexWrap: 'wrap',
                            }}
                          >
                            {doc.tags.map((tag) => (
                              <Chip
                                key={tag.id}
                                label={tag.name}
                                size='small'
                                sx={{
                                  bgcolor: `${getDocumentTagColor(tag.id)}20`,
                                  color: getDocumentTagColor(tag.id),
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              />
                            ))}
                          </Stack>
                        )}

                        {/* File info */}
                        <Typography
                          variant='caption'
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {doc.filename} • {formatFileSize(doc.sizeBytes)} •{' '}
                          {formatDate(doc.createdAt, {
                            omitDay: true,
                            year: '2-digit',
                          })}
                        </Typography>
                      </Stack>
                    </Box>
                  </Card>
                ))}
              </Stack>
            )}

            {/* Empty state */}
            {!isLoading && documents.length === 0 && (
              <Stack
                spacing={2}
                sx={{
                  alignItems: 'center',
                  py: 6,
                }}
              >
                <Icon
                  icon='folder-open'
                  fontSize={48}
                  color={theme.palette.text.disabled}
                />
                <Typography
                  variant='body1'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No documents yet
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  Upload your first document to get started
                </Typography>
              </Stack>
            )}

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
              <DialogTitle>Delete Document</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete "{documentToDelete?.title}"?
                  This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteCancel}>Cancel</Button>
                <Button
                  onClick={handleDeleteConfirm}
                  color='error'
                  variant='contained'
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

KeyDocumentsPage.displayName = 'KeyDocumentsPage';
export default KeyDocumentsPage;
